import { Box, Button, ColumnLayout, Container, Grid } from '@amzn/awsui-components-react';
import { match, useHistory, useRouteMatch } from 'react-router';

import { constants } from '../../../common/Localization';
import { formatCurrencyNoDecimals } from '../../../common/LocalizationHelper';
import { MatchParam } from '../../../common/types';
import { ValueWithLabel } from '../../app-centric/utils';
import { capitalizeWord } from '../../portfolio-data-asset/utils';
import { getTitleToDisplay } from './adjustments-helper';
import { AdjustmentItem, AdjustmentType } from './interfaces';

interface Props {
    currency: string,
    awsAdjustment?: AdjustmentItem,
    onPremAdjustment?: AdjustmentItem,
}

function ViewAdjustmentDetails({ currency, awsAdjustment, onPremAdjustment }: Props): JSX.Element {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/tco/:tcoAssessmentId',
    });

    const tcoAssessmentId = match?.params.tcoAssessmentId ?? null;
    const portfolioId = match?.params.portfolioId ?? null;

    const history = useHistory();
    
    const addAdjustmentBtnClick = () => {
        // TO DO create edit page PAT-32166
        history.push(`/portfolio/${portfolioId}/tco/${tcoAssessmentId}/adjustments/edit/${awsAdjustment?.adjustmentId ?? onPremAdjustment?.adjustmentId}`);
    };

    return (
        <Grid gridDefinition={[{colspan: 6}, {colspan: 6}]}>
            <Container
                data-testid='onPremDetail'
                header={constants.ON_PREMISES}
            >
                { onPremAdjustment ? (
                    <ColumnLayout
                        columns={2}
                    >
                        <ValueWithLabel label={constants.TITLE}>{onPremAdjustment.title}</ValueWithLabel>
                        <ValueWithLabel label={constants.COST_CATEGORY}>{capitalizeWord(onPremAdjustment.category)}</ValueWithLabel>
                        <ValueWithLabel label={constants.DESCRIPTION}>{onPremAdjustment.description}</ValueWithLabel>
                        <ValueWithLabel label={constants.AMOUNT}>
                            {formatCurrencyNoDecimals(onPremAdjustment.amount, currency)}
                        </ValueWithLabel>
                        <ValueWithLabel label={constants.FREQUENCY}>{capitalizeWord(onPremAdjustment.frequency)}</ValueWithLabel>
                        <ValueWithLabel label={constants.IN_SCOPE}>{onPremAdjustment.inScope ? constants.TRUE : constants.FALSE}</ValueWithLabel>
                    </ColumnLayout>
                ) : (
                    <Box textAlign='center'>
                        <Button
                            data-testid='addOnPremBtn'
                            onClick={addAdjustmentBtnClick}
                        >
                            {`${constants.ADD_CORRESPONDING} ${AdjustmentType.ON_PREMISES} ${constants.COST_LOWERCASE}`}
                        </Button>
                    </Box>
                )}
            </Container>
            <Container
                data-testid='awsDetail'
                header={constants.IN_AWS}
            >
                { awsAdjustment ? (
                    <ColumnLayout
                        columns={2}
                    >
                        <ValueWithLabel label={constants.TITLE}>{getTitleToDisplay(awsAdjustment)}</ValueWithLabel>
                        <ValueWithLabel label={constants.COST_CATEGORY}>{capitalizeWord(awsAdjustment.category)}</ValueWithLabel>
                        <ValueWithLabel label={constants.DESCRIPTION}>{awsAdjustment.description}</ValueWithLabel>
                        <ValueWithLabel label={constants.AMOUNT}>{formatCurrencyNoDecimals(awsAdjustment.amount, currency)}</ValueWithLabel>
                        <ValueWithLabel label={constants.FREQUENCY}>{capitalizeWord(awsAdjustment.frequency)}</ValueWithLabel>
                        <ValueWithLabel label={constants.IN_SCOPE}>{awsAdjustment.inScope? constants.TRUE : constants.FALSE}</ValueWithLabel>
                        <ValueWithLabel label={constants.CONSIDERED_FOR_AWS_SUPPORT}>
                            {awsAdjustment.type === AdjustmentType.AWS && awsAdjustment.awsSupport ? constants.TRUE : constants.FALSE}
                        </ValueWithLabel>
                    </ColumnLayout>
                ) : (
                    <Box textAlign='center'>
                        <Button
                            data-testid='addAwsButton'
                            onClick={addAdjustmentBtnClick}
                        >
                            {`${constants.ADD_CORRESPONDING} ${AdjustmentType.AWS} ${constants.COST_LOWERCASE}`}
                        </Button>
                    </Box>
                )}
            </Container>
        </Grid>
    );
};

export default ViewAdjustmentDetails;
